"use client";

import { c as _c } from "react/compiler-runtime";
import { CaretLeft, CaretRight, Funnel, House, MagnifyingGlass } from "@phosphor-icons/react";
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { useState } from "react";
import ActiveFilterChips from "@/components/storefront/ActiveFilterChips";
import CategoryNav from "@/components/storefront/CategoryNav";
import InfiniteProductList from "@/components/storefront/InfiniteProductList";
import ListingSearchBox from "@/components/storefront/ListingSearchBox";
import { ListingTransitionProvider, useListingTransition } from "@/components/storefront/ListingTransitionContext";
import type { CardAction, CardImageAspect, ProductForCard } from "@/components/storefront/ProductCard";
import { ProductGridSkeleton } from "@/components/storefront/ProductCardSkeleton";
import ProductFilters from "@/components/storefront/ProductFilters";
import SortDropdown from "@/components/storefront/SortDropdown";
import { Button } from "@/components/storefront/ui/button";
import { Drawer } from "@/components/storefront/ui/drawer";
import { OverlayCloseButton } from "@/components/storefront/ui/overlay-close-button";
import { Link } from "@/i18n/navigation";
import type { VariantOptionSelection } from "@/lib/storefront/products/listFilterParams";
import { defaultSortForScope } from "@/lib/storefront/products/listFilterParams";
import type { BreadcrumbItem, FacetsDTO, ListingHeroDTO, ListingScope, ListingSort, PaginationDTO, SubcategoryDTO } from "@/lib/storefront/products/listService";
import { SafeImage } from "./SafeImage";
type AppliedFilters = {
  brandSlugs: string[];
  colors: string[];
  tagSlugs: string[];
  categorySlugs: string[];
  variantOptions: VariantOptionSelection;
  minPrice: number | null;
  maxPrice: number | null;
  inStockOnly: boolean;
};

// Reconstruct the ListingScope reference from the hero info we already have.
// InfiniteProductList sends this back to /api/storefront/products/list to
// fetch matching subsequent pages. An `all` scope carries `query` only when
// a search is active, so the client's default sort matches the server's.
export function buildScopeRef(hero: ListingHeroDTO): ListingScope {
  switch (hero.scope_kind) {
    case "search":
      return {
        type: "search",
        query: hero.search_query ?? ""
      };
    case "category":
      return {
        type: "category",
        slug: hero.scope_slug ?? ""
      };
    case "collection":
      return {
        type: "collection",
        slug: hero.scope_slug ?? ""
      };
    case "brand":
      return {
        type: "brand",
        slug: hero.scope_slug ?? ""
      };
    default:
      return hero.search_query ? {
        type: "all",
        query: hero.search_query
      } : {
        type: "all"
      };
  }
}
type Props = {
  hero: ListingHeroDTO;
  products: ProductForCard[];
  facets: FacetsDTO;
  applied: AppliedFilters;
  pagination: PaginationDTO;
  subcategories: SubcategoryDTO[];
  breadcrumb: BreadcrumbItem[];
  locale: string;
  /**
   * Grid card shape. Every listing scope shares this layout and the same card,
   * so all of them (all products, categories, collections, brands, search and
   * the product_listing widget) default to square.
   */
  imageAspect?: CardImageAspect;
  /**
   * Grid card call to action. Every listing scope defaults to the labelled
   * inline button under the price; the compact circle over the image remains
   * available but no listing uses it.
   */
  cardAction?: CardAction;
  /**
   * Whether to render the scope Hero (banner/title/description). Defaults to
   * true so category, collection, search and classic-brand routes — every
   * existing caller — are unaffected. The product_listing builder widget
   * sets this to false because the template it's placed in renders its own
   * hero and would otherwise duplicate the brand banner and `<h1>`.
   */
  showHero?: boolean;
  /**
   * Whether to render the scope Breadcrumb above the hero. Defaults to true
   * so every existing caller keeps its current behaviour. The
   * product_listing builder widget sets this to false because a builder
   * template is not a breadcrumb-scoped route and the crumb would appear as
   * a stray trail mid-page.
   */
  showBreadcrumb?: boolean;
  /**
   * Whether to render the filter sidebar, mobile filter drawer, and the
   * "Filters" toolbar button. Defaults to true — every existing caller keeps
   * its current behaviour. Filters still only render when there are facets
   * to show; this prop can suppress them but never conjure facets that
   * aren't there. The product_listing widget sets this from its config.
   */
  showFilters?: boolean;
  /**
   * Whether to render the sort dropdown in the toolbar. Defaults to true.
   * The product_listing widget sets this from its config.
   */
  showSort?: boolean;
  /**
   * All-products page only: live search box, category tiles and chip bar,
   * and the side filters drop their category tree. Defaults to false so
   * every other listing (and the product_listing widget) is unchanged.
   */
  showCategoryNav?: boolean;
  /**
   * With showCategoryNav: the applied category slugs the server grouped into
   * the tile tree (ProductListingDTO.appliedTreeSlugs). Defaults to [] for
   * every other caller.
   */
  appliedTreeSlugs?: string[];
};
export default function ProductListLayout(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ListingTransitionProvider><ProductListLayoutInner {...props} /></ListingTransitionProvider>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function ProductListLayoutInner(t0) {
  const $ = _c(72);
  const {
    hero,
    products,
    facets,
    applied,
    pagination,
    subcategories,
    breadcrumb,
    imageAspect: t1,
    cardAction: t2,
    showHero: t3,
    showBreadcrumb: t4,
    showFilters: t5,
    showSort: t6,
    showCategoryNav: t7,
    appliedTreeSlugs: t8,
    locale
  } = t0;
  const imageAspect = t1 === undefined ? "square" : t1;
  const cardAction = t2 === undefined ? "inline" : t2;
  const showHero = t3 === undefined ? true : t3;
  const showBreadcrumb = t4 === undefined ? true : t4;
  const showFilters = t5 === undefined ? true : t5;
  const showSort = t6 === undefined ? true : t6;
  const showCategoryNav = t7 === undefined ? false : t7;
  let t9;
  if ($[0] !== t8) {
    t9 = t8 === undefined ? [] : t8;
    $[0] = t8;
    $[1] = t9;
  } else {
    t9 = $[1];
  }
  const appliedTreeSlugs = t9;
  const isRTL = locale === "ar";
  const [filtersOpen, setFiltersOpen] = useState(false);
  const {
    isPending
  } = useListingTransition();
  const hasFacets = facets.brands.length > 0 || facets.colors.length > 0 || facets.tags.length > 0 || !showCategoryNav && facets.categories.length > 0 || facets.optionGroups.length > 0 || facets.priceRange !== null && facets.priceRange.max > facets.priceRange.min;
  const filtersEnabled = showFilters && hasFacets;
  let t10;
  if ($[2] !== isPending) {
    t10 = <PendingBar isPending={isPending} />;
    $[2] = isPending;
    $[3] = t10;
  } else {
    t10 = $[3];
  }
  let t11;
  if ($[4] !== breadcrumb || $[5] !== isRTL || $[6] !== showBreadcrumb) {
    t11 = showBreadcrumb && <Breadcrumb items={breadcrumb} isRTL={isRTL} />;
    $[4] = breadcrumb;
    $[5] = isRTL;
    $[6] = showBreadcrumb;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  let t12;
  if ($[8] !== hero || $[9] !== isRTL || $[10] !== showHero) {
    t12 = showHero && <Hero hero={hero} isRTL={isRTL} />;
    $[8] = hero;
    $[9] = isRTL;
    $[10] = showHero;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== applied || $[13] !== appliedTreeSlugs || $[14] !== facets.categories || $[15] !== hero.search_query || $[16] !== showCategoryNav) {
    t13 = showCategoryNav && <><ListingSearchBox initialQuery={hero.search_query ?? ""} /><CategoryNav categories={facets.categories} appliedSlugs={applied.categorySlugs} treeSlugs={appliedTreeSlugs} /></>;
    $[12] = applied;
    $[13] = appliedTreeSlugs;
    $[14] = facets.categories;
    $[15] = hero.search_query;
    $[16] = showCategoryNav;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== hero.scope_kind || $[19] !== hero.search_query || $[20] !== isRTL) {
    t14 = hero.scope_kind === "search" && <SearchInput initialQuery={hero.search_query ?? ""} isRTL={isRTL} />;
    $[18] = hero.scope_kind;
    $[19] = hero.search_query;
    $[20] = isRTL;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== subcategories) {
    t15 = subcategories.length > 0 && <SubcategoryRow subcategories={subcategories} />;
    $[22] = subcategories;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== hero) {
    t16 = defaultSortForScope(buildScopeRef(hero));
    $[24] = hero;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setFiltersOpen(true);
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== filtersEnabled || $[28] !== isPending || $[29] !== isRTL || $[30] !== locale || $[31] !== pagination.total || $[32] !== showSort || $[33] !== t16) {
    t18 = <Toolbar defaultSort={t16} total={pagination.total} isRTL={isRTL} locale={locale} isPending={isPending} onOpenFilters={t17} showFiltersButton={filtersEnabled} showSort={showSort} />;
    $[27] = filtersEnabled;
    $[28] = isPending;
    $[29] = isRTL;
    $[30] = locale;
    $[31] = pagination.total;
    $[32] = showSort;
    $[33] = t16;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== applied || $[36] !== facets) {
    t19 = <ActiveFilterChips facets={facets} applied={applied} />;
    $[35] = applied;
    $[36] = facets;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t18 || $[39] !== t19) {
    t20 = <div className="mt-6">{t18}{t19}</div>;
    $[38] = t18;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== applied || $[42] !== appliedTreeSlugs || $[43] !== cardAction || $[44] !== facets || $[45] !== filtersEnabled || $[46] !== hero || $[47] !== imageAspect || $[48] !== isPending || $[49] !== isRTL || $[50] !== locale || $[51] !== pagination || $[52] !== products || $[53] !== showCategoryNav) {
    t21 = !isPending && products.length === 0 ? <div className="mt-4"><EmptyState scopeKind={hero.scope_kind} searchQuery={hero.search_query} isRTL={isRTL} treeSlugs={showCategoryNav ? appliedTreeSlugs : []} categorySlugs={applied.categorySlugs} /></div> : <div className={`mt-4 grid grid-cols-1 ${filtersEnabled ? "lg:grid-cols-[260px_1fr]" : ""} gap-6 lg:gap-8 items-start`}>{filtersEnabled && <div className="hidden lg:block lg:sticky lg:top-[calc(var(--storefront-header-h,5rem)+1rem)] lg:max-h-[calc(100vh-var(--storefront-header-h,5rem)-2rem)] lg:overflow-y-auto lg:overscroll-contain"><ProductFilters facets={facets} applied={applied} hideCategoryTree={showCategoryNav} /></div>}<div className="min-w-0">{isPending ? <ProductGridSkeleton count={Math.min(pagination.pageSize, 8)} imageAspect={imageAspect} withAction={cardAction === "inline"} /> : <InfiniteProductList initialProducts={products} initialPagination={pagination} scope={buildScopeRef(hero)} locale={locale} imageAspect={imageAspect} cardAction={cardAction} />}</div></div>;
    $[41] = applied;
    $[42] = appliedTreeSlugs;
    $[43] = cardAction;
    $[44] = facets;
    $[45] = filtersEnabled;
    $[46] = hero;
    $[47] = imageAspect;
    $[48] = isPending;
    $[49] = isRTL;
    $[50] = locale;
    $[51] = pagination;
    $[52] = products;
    $[53] = showCategoryNav;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== applied || $[56] !== facets || $[57] !== filtersEnabled || $[58] !== filtersOpen || $[59] !== isRTL || $[60] !== showCategoryNav) {
    t22 = filtersEnabled && <Drawer isOpen={filtersOpen} onOpenChange={setFiltersOpen} placement="bottom" size="lg" aria-label={isRTL ? "\u0627\u0644\u0641\u0644\u0627\u062A\u0631" : "Filters"}><div className="flex items-center justify-between px-4 py-3 border-b border-border"><h2 className="text-base font-semibold text-foreground">{isRTL ? "\u0627\u0644\u0641\u0644\u0627\u062A\u0631" : "Filters"}</h2><OverlayCloseButton size="sm" onClick={() => setFiltersOpen(false)} /></div><div className="flex-1 overflow-y-auto p-4"><ProductFilters facets={facets} applied={applied} embedded={true} hideCategoryTree={showCategoryNav} /></div></Drawer>;
    $[55] = applied;
    $[56] = facets;
    $[57] = filtersEnabled;
    $[58] = filtersOpen;
    $[59] = isRTL;
    $[60] = showCategoryNav;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== t10 || $[63] !== t11 || $[64] !== t12 || $[65] !== t13 || $[66] !== t14 || $[67] !== t15 || $[68] !== t20 || $[69] !== t21 || $[70] !== t22) {
    t23 = <div className="max-w-[1536px] mx-auto px-4 py-6 md:py-10">{t10}{t11}{t12}{t13}{t14}{t15}{t20}{t21}{t22}</div>;
    $[62] = t10;
    $[63] = t11;
    $[64] = t12;
    $[65] = t13;
    $[66] = t14;
    $[67] = t15;
    $[68] = t20;
    $[69] = t21;
    $[70] = t22;
    $[71] = t23;
  } else {
    t23 = $[71];
  }
  return t23;
}

// ─── Pending bar (top progress indicator) ──────────────────────────

function PendingBar(t0) {
  const $ = _c(1);
  const {
    isPending
  } = t0;
  if (!isPending) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="fixed top-0 inset-x-0 z-50 h-0.5 overflow-hidden pointer-events-none"><div className="h-full bg-primary animate-listing-progress" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  return t1;
}

// ─── Hero ──────────────────────────────────────────────────────────

function Hero(t0) {
  const $ = _c(55);
  const {
    hero,
    isRTL
  } = t0;
  if (hero.scope_kind === "search") {
    const t1 = hero.search_query ? isRTL ? `نتائج البحث عن "${hero.search_query}"` : `Search results for "${hero.search_query}"` : hero.title;
    let t2;
    if ($[0] !== t1) {
      t2 = <h1 className="text-2xl md:text-3xl font-semibold text-foreground">{t1}</h1>;
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== hero.product_count || $[3] !== isRTL) {
      t3 = isRTL ? `${hero.product_count.toLocaleString("ar-u-nu-latn")} نتيجة` : `${hero.product_count.toLocaleString()} ${hero.product_count === 1 ? "result" : "results"}`;
      $[2] = hero.product_count;
      $[3] = isRTL;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t3) {
      t4 = <p className="text-sm text-muted-foreground mt-1">{t3}</p>;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== t2 || $[8] !== t4) {
      t5 = <div className="mb-6">{t2}{t4}</div>;
      $[7] = t2;
      $[8] = t4;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  if (hero.scope_kind === "all") {
    let t1;
    if ($[10] !== hero.title) {
      t1 = <h1 className="text-2xl md:text-3xl font-semibold text-foreground">{hero.title}</h1>;
      $[10] = hero.title;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    let t2;
    if ($[12] !== hero.product_count || $[13] !== isRTL) {
      t2 = isRTL ? `${hero.product_count.toLocaleString("ar-u-nu-latn")} منتج` : `${hero.product_count.toLocaleString()} ${hero.product_count === 1 ? "product" : "products"}`;
      $[12] = hero.product_count;
      $[13] = isRTL;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    let t3;
    if ($[15] !== t2) {
      t3 = <p className="text-sm text-muted-foreground mt-1">{t2}</p>;
      $[15] = t2;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    let t4;
    if ($[17] !== t1 || $[18] !== t3) {
      t4 = <div className="mb-6">{t1}{t3}</div>;
      $[17] = t1;
      $[18] = t3;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    return t4;
  }
  let t1;
  if ($[20] !== hero.product_count || $[21] !== isRTL) {
    t1 = isRTL ? `${hero.product_count.toLocaleString("ar-u-nu-latn")} منتج` : `${hero.product_count.toLocaleString()} ${hero.product_count === 1 ? "product" : "products"}`;
    $[20] = hero.product_count;
    $[21] = isRTL;
    $[22] = t1;
  } else {
    t1 = $[22];
  }
  const countLabel = t1;
  if (hero.image_url) {
    let t2;
    if ($[23] !== hero.blur_data_url || $[24] !== hero.image_url || $[25] !== hero.title) {
      t2 = <SafeImage src={hero.image_url} alt={hero.title} fill={true} className="object-cover" sizes="(max-width: 1536px) 100vw, 1536px" priority={true} blurDataURL={hero.blur_data_url} />;
      $[23] = hero.blur_data_url;
      $[24] = hero.image_url;
      $[25] = hero.title;
      $[26] = t2;
    } else {
      t2 = $[26];
    }
    const t3 = isRTL ? "bg-gradient-to-l from-background/95 via-background/70 to-transparent" : "bg-gradient-to-r from-background/95 via-background/70 to-transparent";
    let t4;
    if ($[27] !== t3) {
      t4 = ["absolute inset-0", t3];
      $[27] = t3;
      $[28] = t4;
    } else {
      t4 = $[28];
    }
    const t5 = t4.join(" ");
    let t6;
    if ($[29] !== t5) {
      t6 = <div className={t5} aria-hidden="true" />;
      $[29] = t5;
      $[30] = t6;
    } else {
      t6 = $[30];
    }
    let t7;
    if ($[31] !== hero.title) {
      t7 = <h1 className="text-2xl md:text-3xl font-semibold text-foreground leading-tight">{hero.title}</h1>;
      $[31] = hero.title;
      $[32] = t7;
    } else {
      t7 = $[32];
    }
    let t8;
    if ($[33] !== hero.description) {
      t8 = hero.description && <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed max-w-prose">{hero.description}</p>;
      $[33] = hero.description;
      $[34] = t8;
    } else {
      t8 = $[34];
    }
    let t9;
    if ($[35] !== countLabel) {
      t9 = <p className="mt-4 text-xs text-muted-foreground uppercase tracking-wide">{countLabel}</p>;
      $[35] = countLabel;
      $[36] = t9;
    } else {
      t9 = $[36];
    }
    let t10;
    if ($[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
      t10 = <div className="relative z-10 max-w-xl px-6 py-10 md:px-12 md:py-14">{t7}{t8}{t9}</div>;
      $[37] = t7;
      $[38] = t8;
      $[39] = t9;
      $[40] = t10;
    } else {
      t10 = $[40];
    }
    let t11;
    if ($[41] !== t10 || $[42] !== t2 || $[43] !== t6) {
      t11 = <div className="relative rounded-2xl overflow-hidden border border-border min-h-[200px] md:min-h-[300px]">{t2}{t6}{t10}</div>;
      $[41] = t10;
      $[42] = t2;
      $[43] = t6;
      $[44] = t11;
    } else {
      t11 = $[44];
    }
    return t11;
  }
  let t2;
  if ($[45] !== hero.title) {
    t2 = <h1 className="text-2xl md:text-3xl font-semibold text-foreground leading-tight">{hero.title}</h1>;
    $[45] = hero.title;
    $[46] = t2;
  } else {
    t2 = $[46];
  }
  let t3;
  if ($[47] !== hero.description) {
    t3 = hero.description && <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed max-w-prose">{hero.description}</p>;
    $[47] = hero.description;
    $[48] = t3;
  } else {
    t3 = $[48];
  }
  let t4;
  if ($[49] !== countLabel) {
    t4 = <p className="mt-4 text-xs text-muted-foreground uppercase tracking-wide">{countLabel}</p>;
    $[49] = countLabel;
    $[50] = t4;
  } else {
    t4 = $[50];
  }
  let t5;
  if ($[51] !== t2 || $[52] !== t3 || $[53] !== t4) {
    t5 = <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-secondary/40 to-background border border-border"><div className="px-6 py-10 md:px-12 md:py-14">{t2}{t3}{t4}</div></div>;
    $[51] = t2;
    $[52] = t3;
    $[53] = t4;
    $[54] = t5;
  } else {
    t5 = $[54];
  }
  return t5;
}

// ─── Breadcrumb ────────────────────────────────────────────────────

function Breadcrumb(t0) {
  const $ = _c(10);
  const {
    items,
    isRTL
  } = t0;
  if (items.length === 0) {
    return null;
  }
  const Caret = isRTL ? CaretLeft : CaretRight;
  const t1 = isRTL ? "\u0645\u0633\u0627\u0631 \u0627\u0644\u062A\u0646\u0642\u0644" : "Breadcrumb";
  let t2;
  if ($[0] !== Caret || $[1] !== isRTL || $[2] !== items) {
    let t3;
    if ($[4] !== Caret || $[5] !== isRTL) {
      t3 = (item, i) => {
        const sep = i > 0 ? <Caret key={`s${i}`} size={12} className="text-muted-foreground/60 shrink-0" /> : null;
        if (item.kind === "home") {
          return <span key="home" className="inline-flex items-center gap-1.5"><Link href="/" className="inline-flex items-center gap-1 hover:text-foreground transition-colors"><House size={12} weight="regular" /><span>{isRTL ? "\u0627\u0644\u0631\u0626\u064A\u0633\u064A\u0629" : "Home"}</span></Link></span>;
        }
        if (item.kind === "current") {
          return <span key="current" className="inline-flex items-center gap-1.5">{sep}<span className="text-primary font-medium truncate max-w-[200px]">{item.name}</span></span>;
        }
        const href = item.kind === "category" ? `/categories/${item.slug}` : item.kind === "brand" ? `/brands/${item.slug}` : `/collections/${item.slug}`;
        return <span key={`${item.kind}-${item.slug}`} className="inline-flex items-center gap-1.5">{sep}<Link href={href} className="hover:text-foreground transition-colors truncate max-w-[180px]">{item.name}</Link></span>;
      };
      $[4] = Caret;
      $[5] = isRTL;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = items.map(t3);
    $[0] = Caret;
    $[1] = isRTL;
    $[2] = items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = <nav aria-label={t1} className="mb-4 flex items-center gap-1.5 text-xs text-muted-foreground flex-wrap">{t2}</nav>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

// ─── Subcategory row ───────────────────────────────────────────────

function SubcategoryRow(t0) {
  const $ = _c(4);
  const {
    subcategories
  } = t0;
  let t1;
  if ($[0] !== subcategories) {
    t1 = subcategories.map(_temp);
    $[0] = subcategories;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <nav aria-label="Subcategories" className="mt-5 -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto scrollbar-hide"><ul className="flex items-center gap-3 min-w-max">{t1}</ul></nav>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

// ─── Toolbar ───────────────────────────────────────────────────────
function _temp(sub) {
  return <li key={sub.id}><Link href={`/categories/${sub.slug}`} className="group flex flex-col items-center gap-2 w-20 shrink-0"><div className="relative h-16 w-16 rounded-full overflow-hidden bg-subtle ring-1 ring-border group-hover:ring-primary transition-colors"><SafeImage src={sub.image_url} alt={sub.name} fill={true} className="object-cover transition-transform duration-300 group-hover:scale-105" sizes="64px" blurDataURL={sub.blur_data_url} fallback={<span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-muted-foreground">{sub.name.slice(0, 1)}</span>} /></div><span className="text-xs leading-4 font-medium text-foreground text-center line-clamp-2 min-h-8 w-full group-hover:text-primary transition-colors">{sub.name}</span></Link></li>;
}
function Toolbar(t0) {
  const $ = _c(24);
  const {
    total,
    isRTL,
    locale,
    defaultSort,
    isPending,
    onOpenFilters,
    showFiltersButton,
    showSort
  } = t0;
  let t1;
  if ($[0] !== isRTL || $[1] !== onOpenFilters || $[2] !== showFiltersButton) {
    t1 = showFiltersButton && <Button type="button" variant="outline" color="default" size="sm" onClick={onOpenFilters} leftIcon={<Funnel />} className="lg:hidden h-[var(--field-height-sm)]">{isRTL ? "\u0627\u0644\u0641\u0644\u0627\u062A\u0631" : "Filters"}</Button>;
    $[0] = isRTL;
    $[1] = onOpenFilters;
    $[2] = showFiltersButton;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = isPending ? "true" : undefined;
  let t3;
  if ($[4] !== isPending) {
    t3 = isPending && <span className="inline-block h-3 w-3 rounded-full border-2 border-primary border-t-transparent animate-spin" aria-hidden="true" />;
    $[4] = isPending;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isPending || $[7] !== isRTL || $[8] !== total) {
    t4 = isPending ? isRTL ? "\u062C\u0627\u0631\u064D \u0627\u0644\u062A\u062D\u062F\u064A\u062B\u2026" : "Updating\u2026" : isRTL ? `${total.toLocaleString("ar-u-nu-latn")} منتج` : `${total.toLocaleString()} ${total === 1 ? "product" : "products"}`;
    $[6] = isPending;
    $[7] = isRTL;
    $[8] = total;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <span className="text-xs text-muted-foreground inline-flex items-center gap-2 transition-opacity" data-pending={t2}>{t3}{t4}</span>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t1 || $[15] !== t5) {
    t6 = <div className="flex items-center gap-3">{t1}{t5}</div>;
    $[14] = t1;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== defaultSort || $[18] !== locale || $[19] !== showSort) {
    t7 = showSort && <SortDropdown locale={locale} defaultSort={defaultSort} />;
    $[17] = defaultSort;
    $[18] = locale;
    $[19] = showSort;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t6 || $[22] !== t7) {
    t8 = <div className="flex items-center justify-between gap-3 flex-wrap">{t6}{t7}</div>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}

// ─── Search input (search scope only) ──────────────────────────────

function SearchInput(t0) {
  const $ = _c(12);
  const {
    initialQuery,
    isRTL
  } = t0;
  const router = useRouter();
  const locale = useLocale();
  const [value, setValue] = useState(initialQuery);
  let t1;
  if ($[0] !== locale || $[1] !== router || $[2] !== value) {
    t1 = function handleSubmit(e) {
      e.preventDefault();
      const trimmed = value.trim();
      if (trimmed) {
        router.push(`/${locale}/search?q=${encodeURIComponent(trimmed)}`);
      }
    };
    $[0] = locale;
    $[1] = router;
    $[2] = value;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleSubmit = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MagnifyingGlass className="absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground" size={18} />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e_0 => setValue(e_0.target.value);
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = isRTL ? "\u0627\u0644\u0628\u062D\u062B \u0639\u0646 \u0645\u0646\u062A\u062C..." : "Search products...";
  let t5;
  if ($[6] !== t4 || $[7] !== value) {
    t5 = <div className="relative">{t2}<input type="search" value={value} onChange={t3} placeholder={t4} className="w-full border border-border rounded-xl py-2.5 ps-10 pe-4 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" /></div>;
    $[6] = t4;
    $[7] = value;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== handleSubmit || $[10] !== t5) {
    t6 = <form onSubmit={handleSubmit} className="mt-6 max-w-xl">{t5}</form>;
    $[9] = handleSubmit;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}

// ─── Empty state ───────────────────────────────────────────────────

function EmptyState(t0) {
  const $ = _c(23);
  const {
    scopeKind,
    searchQuery,
    isRTL,
    treeSlugs,
    categorySlugs
  } = t0;
  const locale = useLocale();
  const {
    navigateWithParams
  } = useListingTransition();
  const canSearchAllTypes = scopeKind === "all" && !!searchQuery && treeSlugs.length > 0;
  let t1;
  if ($[0] !== categorySlugs || $[1] !== navigateWithParams || $[2] !== treeSlugs) {
    t1 = () => navigateWithParams(p => {
      const rest = categorySlugs.filter(s => !treeSlugs.includes(s));
      if (rest.length === 0) {
        p.delete("category");
      } else {
        p.set("category", rest.join(","));
      }
      p.delete("page");
    });
    $[0] = categorySlugs;
    $[1] = navigateWithParams;
    $[2] = treeSlugs;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const searchAllTypes = t1;
  const message = (scopeKind === "search" || scopeKind === "all") && searchQuery ? isRTL ? `لم نجد منتجات تطابق "${searchQuery}"` : `We couldn't find any products matching "${searchQuery}"` : isRTL ? "\u0644\u0627 \u062A\u0648\u062C\u062F \u0645\u0646\u062A\u062C\u0627\u062A \u062A\u0637\u0627\u0628\u0642 \u0627\u0644\u0641\u0644\u0627\u062A\u0631 \u0627\u0644\u0645\u062E\u062A\u0627\u0631\u0629" : "No products match the selected filters";
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MagnifyingGlass size={40} className="text-muted-foreground/40 mb-4" />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = isRTL ? "\u0644\u0627 \u062A\u0648\u062C\u062F \u0646\u062A\u0627\u0626\u062C" : "No results";
  let t4;
  if ($[5] !== t3) {
    t4 = <h2 className="text-base font-semibold text-foreground mb-1.5">{t3}</h2>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== message) {
    t5 = <p className="text-sm text-muted-foreground mb-4 max-w-sm">{message}</p>;
    $[7] = message;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== canSearchAllTypes || $[10] !== isRTL || $[11] !== searchAllTypes) {
    t6 = canSearchAllTypes && <Button type="button" variant="solid" color="primary" size="sm" onClick={searchAllTypes} className="mb-2">{isRTL ? "\u0627\u0628\u062D\u062B \u0641\u064A \u0643\u0644 \u0627\u0644\u0623\u0635\u0646\u0627\u0641" : "Search all types"}</Button>;
    $[9] = canSearchAllTypes;
    $[10] = isRTL;
    $[11] = searchAllTypes;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== locale) {
    t7 = () => window.location.href = `/${locale}`;
    $[13] = locale;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = isRTL ? "\u0627\u0644\u0639\u0648\u062F\u0629 \u0644\u0644\u0631\u0626\u064A\u0633\u064A\u0629" : "Back to Home";
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <Button type="button" variant="outline" color="default" size="sm" onClick={t7}>{t8}</Button>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6 || $[21] !== t9) {
    t10 = <div className="flex flex-col items-center justify-center py-16 text-center">{t2}{t4}{t5}{t6}{t9}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}